// public/v3/src/lib/bhi-scales.js — BHI rating-scale catalog and helpers.
//
// Built-in scales are CMS-validated. Severity bands are clinical thresholds
// and are not editable by the practice. Practices can enable/disable built-ins
// and add custom scales via settings (bhi_enabled_scales, bhi_custom_scales).
//
// Pure functions only — no React, no fetches. Safe to import from any page.
// Module format: CommonJS so Jest can read it directly without an esbuild step.

// severityBands: each tuple is [scoreLow, scoreHigh, severityLabel] (inclusive endpoints).
const BHI_SCALES_BUILTIN = [
  { id: 'PHQ-9',   name: 'PHQ-9 (Depression)',                  maxScore: 27, severityBands: [[0,4,'minimal'],[5,9,'mild'],[10,14,'moderate'],[15,19,'moderately severe'],[20,27,'severe']] },
  { id: 'GAD-7',   name: 'GAD-7 (Anxiety)',                     maxScore: 21, severityBands: [[0,4,'minimal'],[5,9,'mild'],[10,14,'moderate'],[15,21,'severe']] },
  { id: 'AUDIT-C', name: 'AUDIT-C (Alcohol use)',               maxScore: 12, severityBands: [[0,2,'low risk'],[3,7,'at risk'],[8,12,'high risk']] },
  { id: 'DAST-10', name: 'DAST-10 (Drug use)',                  maxScore: 10, severityBands: [[0,0,'no problems'],[1,2,'low'],[3,5,'moderate'],[6,8,'substantial'],[9,10,'severe']] },
  { id: 'PCL-5',   name: 'PCL-5 (PTSD)',                        maxScore: 80, severityBands: [[0,32,'below threshold'],[33,80,'probable PTSD']] },
  { id: 'MDQ',     name: 'MDQ (Mood Disorder Questionnaire)',   maxScore: 13, severityBands: [[0,6,'negative screen'],[7,13,'positive screen']] },
];

function resolveEffectiveScales(settings) {
  const enabledIds = Array.isArray(settings?.bhi_enabled_scales)
    ? settings.bhi_enabled_scales
    : BHI_SCALES_BUILTIN.map((s) => s.id);
  const enabled = BHI_SCALES_BUILTIN.filter((s) => enabledIds.includes(s.id));
  const custom = Array.isArray(settings?.bhi_custom_scales) ? settings.bhi_custom_scales : [];
  return [...enabled, ...custom];
}

function lookupScale(scaleId, settings) {
  if (!scaleId) return null;
  const builtIn = BHI_SCALES_BUILTIN.find((s) => s.id === scaleId);
  if (builtIn) return builtIn;
  const custom = Array.isArray(settings?.bhi_custom_scales) ? settings.bhi_custom_scales : [];
  return custom.find((s) => s.id === scaleId) || null;
}

function computeSeverity(scaleId, score, settings) {
  const scale = lookupScale(scaleId, settings);
  if (!scale || typeof score !== 'number') return '';
  const band = (scale.severityBands || []).find(([lo, hi]) => score >= lo && score <= hi);
  return band ? band[2] : '';
}

module.exports = {
  BHI_SCALES_BUILTIN,
  resolveEffectiveScales,
  lookupScale,
  computeSeverity,
};
